import { TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppState } from 'app/app.state';
import { IApiClaimSearchRequest } from 'app/claims/components/dashboard/components/claim-search/interfaces/api-claim-search-request.interface';
import { IApiClaimSearchResponse } from 'app/claims/components/dashboard/components/claim-search/interfaces/api-claim-search-response.interface';
import { IClaimSearchRequest } from 'app/claims/components/dashboard/components/claim-search/interfaces/claim-search-request.interface';
import { IClaimSearchResponse, IClaimSearchResponseDataItemUI } from 'app/claims/components/dashboard/components/claim-search/interfaces/claim-search-response.interface';
import { IClaimWithdrawal } from 'app/claims/components/dialogs/claim-withdrawal-dialog/interfaces/claim-withdrawal.interface';
import { IApiClaimMessengerConversation } from 'app/claims/components/messenger/interfaces/api-claim-messenger-conversation.interface';
import { IClaimMessageUpdateResponse } from 'app/claims/components/messenger/interfaces/claim-message-update-response.interface';
import { IClaimMessengerConversation } from 'app/claims/components/messenger/interfaces/claim-messenger-conversation.interface';
import { IClaimMessengerMessage } from 'app/claims/components/messenger/interfaces/claim-messenger-message.interface';
import { IClaimMessengerMetadata } from 'app/claims/components/messenger/interfaces/claim-messenger-metadata.interface';
import { IApiClaimCommodityType } from 'app/claims/models/api-claim-commodity-type.interface';
import { IApiClaimStatusHistoryItem } from 'app/claims/models/api-claim-status-history-item.interface';
import { IApiClaimStatusSummary } from 'app/claims/models/api-claim-status-summary.interface';
import { IApiClaimStatus } from 'app/claims/models/api-claim-status.interface';
import { IClaimAdjusterDocument } from 'app/claims/models/claim-adjuster-document.interface';
import { IClaimCommodityType } from 'app/claims/models/claim-commodity-type.interface';
import { IClaimStatusHistoryItem } from 'app/claims/models/claim-status-history-item.interface';
import { IClaimStatusSummary } from 'app/claims/models/claim-status-summary.interface';
import { IClaimStatus } from 'app/claims/models/claim-status.interface';
import { ClaimsConfig } from 'app/claims/services/claims.config';
import { ApiEndpointService } from 'app/core/services/api-endpoint.service';
import { FileUploadChipStatus } from 'app/shared/components/file-upload-chip/enum/file-upload-chip-status.enum';
import { Carriers, ShipmentStatus } from 'app/shared/enum/general-enum';
import { ICountry } from 'app/shared/models/country.interface';
import { Location } from 'app/shared/models/location/location.model';
import { ContactService } from 'app/shared/services/contact/contact.service';
import { FileService } from 'app/shared/services/file/file.service';
import { EnumService } from 'app/shared/services/utility/enum.service';
import { ObjectService } from 'app/shared/services/utility/object.service';
import { UtilityService } from 'app/shared/services/utility/utility.service';
import dayjs from 'dayjs';
import { forkJoin, Observable, of, shareReplay } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import SafeUrlAssembler from 'safe-url-assembler';
import { IClaimDiscard } from '../components/file-claim/components/dialogs/claim-discard-dialog/interfaces/claim-discard.interface';
import { FILE_CLAIM_CONFIG } from '../components/file-claim/config/file-claim.config';
import { claimCommodityTypes } from '../components/file-claim/data/claim-commodity-types';
import { IApiClaimContact } from '../models/api-claim-contact.interface';
import { IApiClaimDeleteResponse } from '../models/api-claim-delete-response.interface';
import { IApiClaimDocumentDeletionResponse } from '../models/api-claim-document-deletion-response.interface';
import { IApiClaimDocumentRequest } from '../models/api-claim-document-request.interface';
import { IApiClaimDocumentResponse } from '../models/api-claim-document-response.interface';
import { IApiClaimDraftResponse } from '../models/api-claim-draft-response.interface';
import { IApiClaimShipment } from '../models/api-claim-shipment.interface';
import { IApiClaimSubmitRequest } from '../models/api-claim-submit-request.interface';
import { IApiClaimSubmitResponse } from '../models/api-claim-submit-response.interface';
import { IApiClaimUploadedDocument } from '../models/api-claim-uploaded-document.interface';
import { IApiClaim } from '../models/api-claim.interface';
import { IClaimContact } from '../models/claim-contact.interface';
import { IClaimDocumentMeta } from '../models/claim-document-meta.interface';
import { ClaimDocumentType } from '../models/claim-document-type.enum';
import { IClaimDocumentUpload } from '../models/claim-document-upload.interface';
import { IClaimMerchandise } from '../models/claim-merchandise.interface';
import { ClaimPackageIssue } from '../models/claim-package-issue.enum';
import { ClaimPackageLocation } from '../models/claim-package-location.enum';
import { ClaimPaymentMethod } from '../models/claim-payment-method.enum';
import { ClaimRecipientContacted } from '../models/claim-recipient-contacted.enum';
import { IClaimShipment } from '../models/claim-shipment.interface';
import { ClaimStatus } from '../models/claim-status.enum';
import { ClaimType } from '../models/claim-type.enum';
import { IClaim } from '../models/claim.interface';
import { IUserContacts } from '../models/user-contacts.interface';
import Big from 'big.js';

@Injectable()
export class ClaimService {
    private userContacts$: Observable<IUserContacts> | null = null;

    public constructor(private readonly http: HttpClient,
                       private readonly apiEndpointService: ApiEndpointService,
                       private readonly enumService: EnumService,
                       private readonly titleCasePipe: TitleCasePipe,
                       private readonly utilityService: UtilityService,
                       private readonly objectService: ObjectService,
                       private readonly fileService: FileService,
                       private readonly contactService: ContactService,
                       private readonly appState: AppState,
    ) {
    }

    public getClaims(): Observable<IClaim[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaims)
            .toString();

        return this.http.get<IClaim[]>(endpoint);
    }

    public findClaimShipments(condition: unknown): Observable<IClaimShipment[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.findClaimShipments)
            .query(condition)
            .toString();

        return this.http.get<IApiClaimShipment[]>(endpoint)
                   .pipe(
                       map(res => this.mapApiClaimShipmentsToClaimShipments(res)),
                   );
    }

    public getClaimDraft(trackingNumber: string): Observable<IApiClaim> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaimDraft)
            .query({ trackingNumber })
            .toString();

        return this.http.get<IApiClaim>(endpoint);
    }

    public getClaim(claimId: string): Observable<IClaim> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaim)
            .param('id', claimId)
            .toString();

        return this.http.get<IClaim>(endpoint);
    }

    public getApiClaimDetails(claimId: string): Observable<IApiClaim> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaimDetails)
            .query({ claimId })
            .toString();

        return this.http.get<IApiClaim>(endpoint);
    }

    public getClaimDetails(claimId: number): Observable<IClaim> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaimDetails)
            .query({ claimId })
            .toString();

        return this.http.get<IApiClaim>(endpoint).pipe(
            map(res => this.mapApiClaimToClaim(res, null, null)),
        );
    }

    public saveClaim(claim: IClaim): Observable<IApiClaimSubmitResponse> {
        const data = this.mapClaimToApiClaimSubmitRequest(claim);
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.createClaim)
            .toString();

        return this.http.post<IApiClaimSubmitResponse>(endpoint, data);
    }

    public saveClaimDraft(claim: IClaim): Observable<IApiClaimDraftResponse> {
        const data = this.mapClaimToApiClaimSubmitRequest(claim);
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.saveClaimDraft)
            .toString();

        return this.http.post<IApiClaimDraftResponse>(endpoint, data);
    }

    public deleteClaim(claimId: number, claimDiscard?: IClaimDiscard): Observable<IApiClaimDeleteResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.deleteClaim)
            .query({
                claimId,
                reason: claimDiscard?.reason,
            })
            .toString();

        return this.http.delete<IApiClaimDeleteResponse>(endpoint);
    }

    public getClaimStatuses(claimIds: number[], force = false): Observable<IClaimStatus[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaimStatuses)
            .query({
                claimIds: claimIds.join(','),
                force,
            })
            .toString();

        return this.http.get<IApiClaimStatus[]>(endpoint)
                   .pipe(
                       map(res => res.map(this.mapApiClaimStatusToClaimStatus.bind(this))),
                   );
    }

    public getClaimStatusSummary(params: IClaimSearchRequest): Observable<IClaimStatusSummary[]> {
        const mappedParams: Partial<IApiClaimSearchRequest> = { ...this.mapClaimSearchRequestToApiClaimSearchRequest(params) };
        delete mappedParams.page;
        delete mappedParams.perPage;

        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaimStatusSummary)
            .query(mappedParams)
            .toString();

        return this.http.get<IApiClaimStatusSummary[]>(endpoint)
                   .pipe(
                       map(res => res.map(this.mapApiClaimStatusSummaryToClaimStatusSummary.bind(this))),
                   );
    }

    public withdrawClaim(claimId: number, data: IClaimWithdrawal): Observable<unknown> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.withdrawClaim)
            .param('id', claimId)
            .toString();

        return this.http.put<unknown>(endpoint, data);
    }

    public deleteClaimDraft(claimId: number, claimDiscard?: IClaimDiscard): Observable<IApiClaimDeleteResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.deleteClaimDraft)
            .query({
                claimId,
                reason: claimDiscard?.reason,
            })
            .toString();

        return this.http.delete<IApiClaimDeleteResponse>(endpoint);
    }

    /**
     * Retrieves the claim documents associated with a specific claim ID.
     *
     * @param {number} claimId - The unique identifier for the claim.
     * @return {Observable<IApiClaimDocumentResponse[]>} An observable containing the list of claim documents.
     */
    public getClaimDocuments(claimId: number): Observable<IApiClaimDocumentResponse[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaimDocuments)
            .query({ claimId })
            .toString();

        return this.http.get<IApiClaimDocumentResponse[]>(endpoint);
    }

    /**
     * Uploads a list of claim documents to the server.
     *
     * This method takes an array of claim documents, converts the files to Base64 format,
     * and sends them, along with metadata, to the server for uploading to the specified claim ID.
     *
     * @param {number} claimId - The unique identifier of the claim to associate the uploaded documents with.
     * @param {IClaimDocumentUpload[]} documents - An array of documents to be uploaded. Each document includes the file, filename, and document type metadata.
     * @return {Observable<IApiClaimDocumentResponse[]>} An observable emitting an array of responses from the server after the upload process is completed.
     */
    public uploadClaimDocuments(claimId: number,
                                documents: IClaimDocumentUpload[],
    ): Observable<IApiClaimDocumentResponse[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.uploadClaimDocuments)
            .toString();

        const apiDocumentObservables = documents.map(document =>
            this.fileService.convertFileToBase64(document.file).pipe(
                map(base64 => ({
                    fullFileName: document.fileName,
                    base64,
                    documentType: document.documentType,
                } as IApiClaimDocumentRequest)),
            ),
        );

        return forkJoin(apiDocumentObservables)
            .pipe(
                map(apiDocuments => ({
                    claimId,
                    documents: apiDocuments,
                })),
                switchMap(data => this.http.post<IApiClaimDocumentResponse[]>(endpoint, data)),
            );
    }

    /**
     * Uploads claim documents as form data to the specified endpoint.
     *
     * @param claimId The unique identifier of the claim.
     * @param documents An array of documents to be uploaded, including their metadata and files.
     * @return An observable that emits an array of responses for the uploaded documents upon success.
     */
    public uploadClaimDocumentsAsFormData(claimId: number, documents: IClaimDocumentUpload[]): Observable<IApiClaimDocumentResponse[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.uploadClaimDocuments)
            .toString();

        const formData = new FormData();
        formData.append('claimId', claimId.toString());

        const data: IApiClaimDocumentRequest[] = documents
            .filter(document => !!document?.file)
            .map(document => ({
                fullFileName: document.fileName,
                documentType: document.documentType,
            }));
        const documentData = { documents: data };
        formData.append('body', JSON.stringify(documentData));

        const files = documents
            .filter(document => !!document?.file)
            .map(document => ({ file: document.file, fileName: document.fileName }));
        for (const file of files) {
            formData.append('files', file.file, file.fileName);
        }

        return this.http.post<IApiClaimDocumentResponse[]>(endpoint, formData);
    }

    /**
     * Uploads a claim document as form data to the specified endpoint.
     *
     * @param {number} claimId - The unique identifier of the claim for which the document is being uploaded.
     * @param {IClaimDocumentUpload} document - An object containing the details of the document to be uploaded such as type, file, and filename.
     * @return {Observable<IApiClaimDocumentResponse>} An observable that emits the response of the server after the file upload operation.
     */
    public uploadClaimDocumentAsFormData(claimId: number, document: IClaimDocumentUpload): Observable<IApiClaimDocumentResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.claims.documents.uploadSingle)
            .toString();

        const formData = new FormData();
        formData.append('claimId', claimId.toString());
        formData.append('documentType', document.documentType);
        formData.append('file', document.file, document.fileName);

        return this.http.post<IApiClaimDocumentResponse>(endpoint, formData);
    }

    /**
     * Uploads a single claim document as form data to the specified endpoint.
     *
     * @param {number} claimId - The unique identifier of the claim to which the document belongs.
     * @param {IClaimDocumentUpload} document - An object containing details of the document being uploaded, including the file, file name, and document type.
     * @return {Observable<IApiClaimDocumentResponse[]>} An observable that emits an array of responses for the uploaded document.
     */
    public uploadSingleClaimDocumentAsFormData(claimId: number, document: IClaimDocumentUpload): Observable<IApiClaimDocumentResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.uploadClaimDocuments)
            .toString();

        const formData = new FormData();
        formData.append('claimId', claimId.toString());

        const documentData = { documents: [{ fullFileName: document.fileName, documentType: document.documentType }] };
        formData.append('body', JSON.stringify(documentData));

        formData.append('files', document.file, document.fileName);

        return this.http.post<IApiClaimDocumentResponse[]>(endpoint, formData)
                   .pipe(
                       map(res => res[0]),
                   );
    }

    public deleteClaimDocuments(claimId: number, documentIds: string[]): Observable<IApiClaimDocumentDeletionResponse[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.deleteClaimDocuments)
            .query({
                claimId,
                documentIds: documentIds.join(','),
            })
            .toString();

        return this.http.delete<IApiClaimDocumentDeletionResponse[]>(endpoint);
    }

    public deleteClaimDocument(claimId: number, documentId: string): Observable<IApiClaimDocumentDeletionResponse[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.deleteClaimDocument)
            .query({
                claimId,
                documentId,
            })
            .toString();

        return this.http.delete<IApiClaimDocumentDeletionResponse[]>(endpoint);
    }

    public canFileClaim(claim: IClaim): boolean {
        if (!claim) {
            return false;
        }

        const isAllowed = FILE_CLAIM_CONFIG.claimableClaimStatuses.includes(claim?.status)
            // && FILE_CLAIM_CONFIG.claimableTrackingStatuses.includes(claim?.shipment?.trackingStatus)
            && FILE_CLAIM_CONFIG.claimableShipmentStatuses.includes(claim?.shipment?.shipmentStatus);

        if (!isAllowed) {
            if (!FILE_CLAIM_CONFIG.claimableClaimStatuses.includes(claim?.status)) {
                console.warn(`Filing a claim with the claim status of ${ this.enumService.getEnumKeyByValue(ClaimStatus, claim?.status) } is not allowed.`);
            }

            if (!FILE_CLAIM_CONFIG.claimableShipmentStatuses.includes(claim?.shipment?.shipmentStatus)) {
                console.warn(`Filing a claim with the shipment status of ${ this.enumService.getEnumKeyByValue(ShipmentStatus, claim?.shipment?.shipmentStatus) } is not allowed.`);
            }

            // Handle the case where the data is inconsistent.
            // For example, a draft was saved at some point in time, and now the shipment has become too old.
            // The (API) claim status might be "draft" with a status description, but we no longer allow a user to file a claim for it.
            // In this case, the internal (UI) claim status will be updated accordingly by ClaimService.validateClaimShipment() before reaching this point,
            // and we will show a generic error message instead of the claim status description from the API response.
            const genericErrorMessage =
                `${ this.titleCasePipe.transform(
                    this.utilityService.autoLabelFromKey(this.enumService.getEnumKeyByValue(ClaimStatus, claim?.status)),
                ) }: 
                The shipment date is invalid for online claim submission. Please contact Customer Service customerservice@parcelpro.com.`;
            // MV3-7272: Do not show a warning message at the top and display new message underneath tracking number input
            const errorMessage = [
                ClaimStatus.MISSING_SHIPMENT,
                ClaimStatus.INVALID_INSURED_VALUE,
                ClaimStatus.INVALID_CARRIER,
                ClaimStatus.INVALID_SHIP_DATE,
            ].includes(claim?.status)
                ? genericErrorMessage
                : claim?.statusDescription || claim?.shipment?.claimStatusDescription;

            // this.notificationService.notify(
            //     errorMessage,
            //     'Claim Filing Not Allowed',
            //     NotificationType.WARNING,
            // );
        }

        return isAllowed;
    }

    public validateClaimShipment(claim: IClaim): void {
        if (!claim) {
            return;
        }

        if (!claim?.shipment) {
            claim.status = ClaimStatus.MISSING_SHIPMENT;
            return;
        }

        const isLowInsuredValue = claim.shipment.insuredValue <= FILE_CLAIM_CONFIG.lowInsuredValueThreshold;
        const isUnsupportedCarrier = claim.shipment.inputType?.trim().toLowerCase() === 'web'
            && !FILE_CLAIM_CONFIG.allowedWebShipmentCarriers.includes(claim.shipment.carrier);
        const isOldShipment = claim.shipment.shipDate
            && dayjs(claim.shipment.shipDate).isBefore(dayjs().subtract(FILE_CLAIM_CONFIG.timeWindow.number, FILE_CLAIM_CONFIG.timeWindow.unit));

        switch (true) {
            case isLowInsuredValue:
                claim.status = ClaimStatus.INVALID_INSURED_VALUE;
                break;
            case isUnsupportedCarrier:
                claim.status = ClaimStatus.INVALID_CARRIER;
                break;
            case isOldShipment:
                claim.status = ClaimStatus.INVALID_SHIP_DATE;
                break;
            default:
                break;
        }
    }

    public mapShipmentToClaim(shipment: IClaimShipment, accountPayableContact: Location): IClaim {
        if (!shipment) {
            return null;
        }

        const claimantContact = { ...shipment?.shipperContact };
        claimantContact.companyName ||= accountPayableContact?.CompanyName;
        claimantContact.firstName ||= accountPayableContact?.FirstName;
        claimantContact.lastName ||= accountPayableContact?.LastName;
        claimantContact.address1 ||= accountPayableContact?.StreetAddress;
        claimantContact.address2 ||= accountPayableContact?.ApartmentSuite;
        claimantContact.city ||= accountPayableContact?.City;
        claimantContact.state ||= accountPayableContact?.State;
        claimantContact.zip ||= accountPayableContact?.Zip;
        claimantContact.countryCode ||= accountPayableContact?.Country;
        claimantContact.phone ||= accountPayableContact?.TelephoneNo?.replace(/^\+/, '');
        claimantContact.email ||= accountPayableContact?.Email;

        return {
            claimId: null,
            status: shipment.claimStatus,
            statusDescription: shipment.claimStatusDescription,
            customerId: shipment.customerId,
            claimantContact,
            shipment: shipment,
            claimDetails: null,
            merchandises: [],
            packaging: null,
            additionalInformation: null,
            documentUploads: [],
            payeeInformation: null,
        } as IClaim;
    }

    public mapApiClaimToClaim(apiClaim: IApiClaim, shipment: IClaimShipment, userContacts: IUserContacts): IClaim {
        const latestClaimStatus = apiClaim.claimStatus
            || apiClaim.claimStatuses?.slice().sort((a, b) => {
                if (!a.activityDate) {
                    return 1;
                }

                if (!b.activityDate) {
                    return -1;
                }
            })?.[0]?.status;

        return {
            additionalInformation: {
                recipientContacted: this.enumService.getEnumByValue(ClaimRecipientContacted, apiClaim.recipientContacted?.toLowerCase()),
                packageLocation: this.enumService.getEnumByValue(ClaimPackageLocation, apiClaim.packageCurrentLocation),
                details: apiClaim.incidentNotes,
            },
            // adjusterDocuments: apiClaim.documents?.map(document => ({
            //     fileName: document.name,
            //     mimeType: document.mimeType,
            //     dateModified: document.dateModified,
            //     author: document.author,
            //     documentType: document.documentType,
            // }) as IClaimAdjusterDocument) || [],
            adjusterDocuments: apiClaim.uploadedFiles
                                       ?.filter(file => file.isAdjuster)
                                       ?.map(file => ({
                                           fileName: file.filename,
                                           mimeType: file.mimeType,
                                           dateModified: file.submitDate,
                                           documentType: file.documentType,
                                       }) as IClaimAdjusterDocument) || [],
            claimDetails: {
                claimType: this.enumService.getEnumByValue(ClaimType, apiClaim.claimType),
                claimPackageIssue: this.enumService.getEnumByValue(ClaimPackageIssue, apiClaim.packageIssue),
            },
            claimId: apiClaim.claimId,
            claimNumber: apiClaim.claimNumber,
            claimantContact: this.mapApiClaimContactToClaimContact(apiClaim.claimantContactInfo, userContacts),
            customerId: apiClaim.claimantContactInfo?.customerId,
            documentUploads: this.mapApiUploadedDocumentsToDocuments(apiClaim.uploadedFiles?.filter(file => !file.isAdjuster)),
            merchandises: apiClaim.products.map(product => ({
                commodityTypeCode: product.productCategory,
                description: product.productDescription,
                numberOfItems: product.noOfItems,
                itemNumber: product.itemModelNo,
                serialNumber: product.serialNo,
                invoiceValue: product.invoiceValue,
                invoiceValueCurrency: this.getClaimCurrency(),
                claimedAmount: product.claimedAmount,
                claimedAmountCurrency: this.getClaimCurrency(),
            }) as IClaimMerchandise) || [],
            packaging: {
                hasOuterBox: !!apiClaim.packageDescription?.outerBox?.trim(),
                hasInnerBox: !!apiClaim.packageDescription?.innerBox?.trim(),
                hasOtherPackaging: !!apiClaim.packageDescription?.other?.trim(),
                outerBoxDescription: apiClaim.packageDescription?.outerBox,
                innerBoxDescription: apiClaim.packageDescription?.innerBox,
                otherPackagingDescription: apiClaim.packageDescription?.other,
            },
            payeeInformation: {
                paymentMethod: this.enumService.getEnumByValue(ClaimPaymentMethod, apiClaim.claimantPayeeInfo?.paymentMethod),
                payeeAccountSettings: {
                    companyName:
                        apiClaim.claimantPayeeInfo?.companyName
                        || userContacts?.accountPayableContact?.CompanyName
                        || userContacts?.mailingContact?.CompanyName,
                    firstName:
                        apiClaim.claimantPayeeInfo?.firstName
                        || userContacts?.accountPayableContact?.FirstName
                        || userContacts?.mailingContact?.FirstName,
                    lastName:
                        apiClaim.claimantPayeeInfo?.lastName
                        || userContacts?.accountPayableContact?.LastName
                        || userContacts?.mailingContact?.LastName,
                },
                mailingAddress: this.mapApiMailingAddressToMailingAddress(apiClaim.claimantPayeeInfo, userContacts?.mailingContact),
            },
            paidAmount: apiClaim.paidAmount,
            shipment: {
                // carrier: this.enumService.getEnumByKey(Carriers, apiClaim.carrier),
                carrier: this.enumService.getEnumByKey(Carriers, this.titleCasePipe.transform(apiClaim.carrier)),
                customerId: apiClaim.claimantContactInfo?.customerId,
                claimStatus: this.enumService.getEnumByValue(ClaimStatus, latestClaimStatus),
                deliveredTo: [
                    apiClaim.recipientContactInfo.city,
                    apiClaim.recipientContactInfo.state,
                    apiClaim.recipientContactInfo.zip,
                ].filter(Boolean).join(', '),
                inputType: shipment?.inputType,
                insuredValue: apiClaim.insuredValue,
                currencyCode: this.getClaimCurrency(),
                receiverContact: this.mapApiClaimContactToClaimContact(apiClaim.recipientContactInfo),
                shipDate: apiClaim.shipDate,
                shipmentStatus: this.enumService.getEnumByValue(ShipmentStatus, shipment?.shipmentStatus),
                shipperContact: this.mapApiClaimContactToClaimContact(apiClaim.shipperContactInfo),
                trackingNumber: apiClaim.trackingNumber,
                latestTrackingScan: {
                    activityDate: shipment?.latestTrackingScan?.activityDate || 'N/A',
                    signedForBy: shipment?.latestTrackingScan?.signedForBy,
                    statusTypeCode: apiClaim.trackingStatus || (apiClaim.shipmentStatus === 6 ? 'D' : 'I'),
                },
            },
            // shipmentStatus: this.enumService.getEnumByValue(ShipmentStatus, apiClaim.shipmentStatus),
            status: this.enumService.getEnumByValue(ClaimStatus, latestClaimStatus),
            statusDescription: shipment?.claimStatusDescription,
            statusHistory: this.mapApiClaimStatusHistoryToClaimStatusHistory(apiClaim.claimStatuses),
            trackingNumber: apiClaim.trackingNumber,
        } as IClaim;
    }

    /**
     * Retrieves a list of countries to which a carrier can ship, based on the provided selection type.
     *
     * @param {Carriers | string} carrier - The carrier for which the shipping countries are to be retrieved.
     * It can be a value from the Carriers enum or a string representation.
     * @param {'address' | 'location'} selectionType - Specifies whether to retrieve countries for 'address' (for ShipTo countries) or 'location' (for ShipFrom countries) based selection.
     * @return {Observable<ICountry[]>} An observable that emits the list of countries available for shipping by the specified carrier.
     */
    public getShipmentCountries(carrier: Carriers | string, selectionType: 'address' | 'location'): Observable<ICountry[]> {
        let carrierString: string = carrier as any;
        if (this.enumService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.enumService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getCarrierCountries)
            .query({
                selectionType,
                carriercode: carrierString.toLowerCase(),
                isEu: false,
            })
            .toString();

        return this.http.get<ICountry[]>(endpoint)
                   .pipe(
                       map((countries) => this.sortCountries(countries)),
                   );
    }

    public mapApiClaimDocumentToClaimDocumentMeta(apiDocument: IApiClaimDocumentResponse): IClaimDocumentMeta {
        if (!apiDocument) {
            return null;
        }

        const extension = this.fileService.getExtensionFromFileName(apiDocument.fileName);
        const mimeType = this.fileService.getMimeTypeFromExtension(extension);

        return {
            documentId: apiDocument.documentId,
            fileName: apiDocument.fileName,
            fileType: mimeType,
            documentType: this.enumService.getEnumByValue(ClaimDocumentType, apiDocument.documentType),
        } as IClaimDocumentMeta;
    }

    public mapApiClaimDocumentToClaimDocumentUpload(apiDocument: IApiClaimDocumentResponse): IClaimDocumentUpload {
        if (!apiDocument) {
            return null;
        }

        const extension = this.fileService.getExtensionFromFileName(apiDocument.fileName);
        const mimeType = this.fileService.getMimeTypeFromExtension(extension);

        return {
            file: null,
            mimeType,
            documentId: apiDocument.documentId,
            fileName: apiDocument.fileName,
            documentType: this.enumService.getEnumByValue(ClaimDocumentType, apiDocument.documentType),
            isPendingDelete: false,
        } as IClaimDocumentUpload;
    }

    /**
     * Retrieves and caches user contact details, including account payable
     * contacts and mailing contacts.
     *
     * This method ensures that multiple subscribers share the same API response
     * by using `shareReplay(1)`, preventing duplicate network requests.
     *
     * @returns {Observable<IUserContacts>} An observable emitting user contacts,
     * including the first account payable contact and the first mailing contact.
     */
    public getUserContacts(): Observable<IUserContacts> {
        if (!this.userContacts$) {
            this.userContacts$ = forkJoin({
                accountPayableContacts: this.contactService.getContactAp('false'),
                mailingContacts: this.contactService.getMailingContact(),
            }).pipe(
                /**
                 * Ensures that multiple subscribers share the same result and
                 * prevents unnecessary duplicate API requests.
                 * `refCount: true` will automatically clear the observable when there is no subscriber.
                 */
                shareReplay({ bufferSize: 1, refCount: true }),
                map((res) => ({
                        accountPayableContact: res.accountPayableContacts?.[0],
                        mailingContact: res.mailingContacts?.[0],
                    } as IUserContacts),
                ),
            );
        }

        // Return the cached observable to prevent duplicate requests
        return this.userContacts$;
    }

    public getClaimCommodityTypes(): Observable<IClaimCommodityType[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getClaimCommodityTypes)
            .toString();

        return this.http.get<IApiClaimCommodityType[]>(endpoint)
                   .pipe(
                       map((commodityTypes) => commodityTypes.map(item => this.mapApiClaimCommodityTypeToClaimCommodityType(item))),
                       catchError((error) => {
                           console.error(error);
                           return of(claimCommodityTypes);
                       }),
                   );
    }

    public removeConsecutiveDuplicateClaimHistoryStatuses(items: IClaimStatusHistoryItem[]): IClaimStatusHistoryItem[] {
        return items.filter((item, index, arr) => {
            return index === 0 || item.status !== arr[index - 1].status;
        });
    }

    public getMessengerConversationMetadata(claimId: number): Observable<IClaimMessengerMetadata> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.claims.conversations.metadata)
            .param('claimId', claimId)
            .toString();

        return this.http.get<IClaimMessengerMetadata>(endpoint);
    }

    /**
     * Fetches a cached conversation for a specific claim and optionally filters by a topic.
     *
     * @param {number} claimId - The unique identifier of the claim for which the conversation is to be retrieved.
     * @param {string} [topic] - An optional parameter to specify the conversation topic to filter by.
     * @return {Observable<IClaimMessengerConversation>} An observable emitting the conversation associated with the given claim ID and topic.
     */
    public getMessengerCachedConversation(claimId: number, topic?: string): Observable<IClaimMessengerConversation> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.claims.conversations.get)
            .param('claimId', claimId)
            .query({
                ...(topic !== null && topic !== undefined && topic !== '' && { topic }),
            })
            .toString();

        return this.http.get<IApiClaimMessengerConversation>(endpoint)
                   .pipe(
                       map(res => this.mapApiConversationToConversation(res, topic)),
                   );
    }

    /**
     * Synchronizes the messenger conversation from Guidewire for the given claim with an optional topic parameter.
     *
     * @param {number} claimId - The unique identifier of the claim whose conversation is to be synchronized.
     * @param {string} [topic] - An optional topic to filter or categorize the conversation.
     * @return {Observable<IClaimMessengerConversation>} An observable emitting the synchronized claim messenger conversation object.
     */
    public syncMessengerConversation(claimId: number, topic?: string): Observable<IClaimMessengerConversation> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.claims.conversations.sync)
            .param('claimId', claimId)
            .query({
                ...(topic !== null && topic !== undefined && topic !== '' && { topic }),
            })
            .toString();

        return this.http.post<IClaimMessengerConversation>(endpoint, {})
                   .pipe(
                       map(res => this.mapApiConversationToConversation(res, topic)),
                   );
    }

    public createConversationMessage(claimId: number, message: IClaimMessengerMessage): Observable<IClaimMessageUpdateResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.claims.conversations.createMessage)
            .param('claimId', claimId)
            .toString();

        return this.http.post<IClaimMessageUpdateResponse>(endpoint, message);
    }

    public updateConversationMessage(claimId: number, message: IClaimMessengerMessage): Observable<IClaimMessageUpdateResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.claims.conversations.updateMessage)
            .param('claimId', claimId)
            .query({ messageId: message?.messageId })
            .toString();

        return this.http.put<IClaimMessageUpdateResponse>(endpoint, message);
    }

    public hasClaims(): Observable<{ hasClaims: boolean }> {
        // const endpoint = SafeUrlAssembler()
        //     .template(this.apiEndpointService.endpointUrls.hasClaims)
        //     .toString();
        //
        // return this.http.get<{ hasClaims: boolean }>(endpoint);

        const params = {
            pageIndex: 0,
            pageSize: 1,
        };

        return this.findClaims(params)
                   .pipe(
                       map(response => ({ hasClaims: response.claims.length > 0 })),
                   );
    }

    public findClaims(params: IClaimSearchRequest): Observable<IClaimSearchResponse> {
        const mappedParams = this.mapClaimSearchRequestToApiClaimSearchRequest(params);
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.findClaims)
            .query(mappedParams)
            .toString();

        return this.http.get<IClaimSearchResponse>(endpoint)
                   .pipe(
                       shareReplay(1),
                       map(response => this.mapApiClaimSearchResponseToClaimSearchResponse(response, params.pageIndex, params.pageSize)),
                   );
    }

    public downloadClaims(params: IClaimSearchRequest): Observable<Blob> {
        const mappedParams = this.mapClaimSearchRequestToApiClaimSearchRequest(params);
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.downloadClaims)
            .query(mappedParams)
            .toString();
        return this.http.get(endpoint, { responseType: 'blob' });
    }

    public sendEmail(claim: IClaimSearchResponseDataItemUI): void {
        const getClaimSubject = (claim?: { claimNumber?: string; claimStatus?: string }): string => {
            if (!claim?.claimNumber) {
                return 'Claim - Filing';
            }

            const statusMap: Record<string, string> = {
                draft: 'Claim - Draft',
                // Add more statuses here:
                // pending: 'Claim - Pending Review',
                // rejected: 'Claim - Rejected',
            };

            return statusMap[claim.claimStatus ?? ''] || `Claim - ${ claim.claimNumber }`;
        };

        const subject = encodeURIComponent(getClaimSubject(claim));

        const customerId = this.appState.customer$().CustomerId;
        const body = encodeURIComponent(`Customer ID: ${ customerId }\nTracking Number :${ claim?.trackingNumber }`);
        const email = ClaimsConfig.supportEmail;
        window.location.href = `mailto:${ email }?subject=${ subject }&body=${ body }`;
    }

    private mapApiConversationToConversation(apiConversation: IApiClaimMessengerConversation, topic = 'general'): IClaimMessengerConversation {
        if (!apiConversation) {
            return null;
        }

        const topicData = apiConversation.messagesByTopic.find(message => message.topic === topic);

        return {
            claimId: apiConversation.claimId,
            claimNumber: apiConversation.claimNumber,
            totalCount: apiConversation.totalCount,
            messages: topicData?.messages || [],
            syncStatus: topicData?.syncStatus,
            unreadAdjusterCount: topicData?.unreadAdjusterCount,
        } as IClaimMessengerConversation;
    }

    private mapApiClaimStatusToClaimStatus(apiClaimStatus: IApiClaimStatus): IClaimStatus {
        return {
            ...apiClaimStatus,
            status: this.enumService.getEnumByValue(ClaimStatus, apiClaimStatus.status),
        };
    }

    private mapClaimSearchRequestToApiClaimSearchRequest(request: IClaimSearchRequest): IApiClaimSearchRequest {
        const req: IApiClaimSearchRequest = {
            page: request.pageIndex + 1,
            perPage: request.pageSize,
            claimNumber: request.claimNumber,
            claimStatus: request.claimStatus === '' ? null : request.claimStatus,
            minDate: request.minSubmitDate && dayjs(request.minSubmitDate).isValid()
                ? dayjs(request.minSubmitDate).toISOString()
                : null,
            maxDate: request.maxSubmitDate && dayjs(request.maxSubmitDate).isValid()
                ? dayjs(request.maxSubmitDate).toISOString()
                : null,
            minAmount: request.minClaimedAmount,
            maxAmount: request.maxClaimedAmount,
            hasNewMessages: request.hasNewMessages,
        };

        return this.objectService.removeNullish(req as Record<string, unknown>) as IApiClaimSearchRequest;
    }

    private mapApiClaimSearchResponseToClaimSearchResponse(response: IApiClaimSearchResponse,
                                                           pageIndex: number,
                                                           pageSize: number,
    ): IClaimSearchResponse {
        const res: IClaimSearchResponse = {
            claims: [],
            pagination: {
                pageIndex,
                pageSize,
                length: response.totalClaims,
            },
        };

        for (const claim of response.claims) {
            const mappedClaim = {
                // claimId: claim.claimId,
                // claimNumber: claim.claimNumber,
                // claimStatus: claim.claimStatus,
                // claimedAmount: claim.claimedAmount,
                // paidAmount: claim.paidAmount,
                // receiver: claim.receiver,
                ...claim,
                lastStatusCheckDate: claim.lastStatusUpdateAt,
                submitDate: claim.fileDate,
                // trackingNumber: claim.trackingNumber,
            };

            res.claims.push(mappedClaim);
        }

        return res;
    }

    private mapApiClaimStatusSummaryToClaimStatusSummary(apiClaimStatusSummary: IApiClaimStatusSummary): IClaimStatusSummary {
        return {
            status: this.enumService.getEnumByValue(ClaimStatus, apiClaimStatusSummary.statusCode),
            count: apiClaimStatusSummary.count,
        } as IClaimStatusSummary;
    }

    private mapApiClaimCommodityTypeToClaimCommodityType(apiClaimCommodityType: IApiClaimCommodityType): IClaimCommodityType {
        return {
            commodityTypeId: apiClaimCommodityType.productCategoryId,
            typeCode: apiClaimCommodityType.code,
            displayedText: apiClaimCommodityType.name,
        } as IClaimCommodityType;
    }

    private mapApiClaimStatusHistoryToClaimStatusHistory(apiClaimStatusHistory: IApiClaimStatusHistoryItem[]): IClaimStatusHistoryItem[] {
        return apiClaimStatusHistory.map(item => ({
            status: this.enumService.getEnumByValue(ClaimStatus, item.status),
            activityDate: dayjs(item.activityDate).isValid() ? dayjs(item.activityDate).toDate() : null,
        }));
    }

    private sortCountries(countries: ICountry[]): ICountry[] {
        return countries.sort((a, b) => {
            // Sort by CountryName first
            if (a.CountryName < b.CountryName) {
                return -1;
            }

            if (a.CountryName > b.CountryName) {
                return 1;
            }

            // If CountryName is the same, sort by MaxCoverage
            return a.MaxCoverage - b.MaxCoverage;
        });
    }

    private mapApiClaimShipmentsToClaimShipments(shipments: IApiClaimShipment[]): IClaimShipment[] {
        return shipments?.map(shipment => ({
            customerId: shipment.customerId,
            claimStatus: shipment.claimStatus,
            claimStatusDescription: shipment.claimStatusDescription,
            trackingNumber: shipment.trackingNumber,
            carrier: this.enumService.getEnumByKey(Carriers, this.titleCasePipe.transform(shipment.carrierCode)),
            shipmentStatus: <ShipmentStatus>shipment.status,
            shipDate: dayjs(shipment.shipDate)?.toDate(),
            deliveredTo: [shipment.shipTo.city, shipment.shipTo.state, shipment.shipTo.zip].filter(Boolean).join(', '),
            inputType: shipment.inputType,
            insuredValue: shipment.insuredValue,
            currencyCode: shipment.currencyCode || 'USD',
            shipperContact: this.mapClaimContact(shipment.shipFrom),
            receiverContact: this.mapClaimContact(shipment.shipTo),
            latestTrackingScan: {
                activityDate: shipment.activityDate || 'N/A',
                signedForBy: shipment.signedForBy,

                // For old data, there is likely no tracking scan data.
                // In this case, we look at the shipment status instead.
                // If it's 6, it's "Delivered"; otherwise, we just treat it as "In Transit".
                statusTypeCode: shipment.trackingStatus || (shipment.status === 6 ? 'D' : 'I'),
            },
        }) as IClaimShipment) ?? [];
    }

    private mapClaimContact(contact: IApiClaimContact): IClaimContact {
        if (!contact) {
            return null;
        }

        const trimmed = Object.fromEntries(Object.entries(contact).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v]));

        return {
            companyName: trimmed.companyName,
            firstName: trimmed.firstName,
            lastName: trimmed.lastName,
            phone: trimmed.telephoneNo?.replace(/^\+/, ''),
            email: trimmed.email,
            address1: trimmed.streetAddress,
            address2: trimmed.apartmentSuite,
            city: trimmed.city,
            state: trimmed.state,
            zip: trimmed.zip,
            countryCode: trimmed.country,
        } as IClaimContact;
    }

    private mapClaimToApiClaimSubmitRequest(claim: IClaim): IApiClaimSubmitRequest {
        if (!claim) {
            return null;
        }

        return {
            claimId: claim.claimId,
            claimDetails: {
                trackingNumber: claim.shipment?.trackingNumber,
                shipDate: dayjs(claim.shipment?.shipDate).format('YYYY-MM-DD'),
                lossCauseCode: claim.claimDetails?.claimType,
                insuredValue: claim.shipment?.insuredValue,
                carrier: this.enumService.getEnumKeyByValue(Carriers, claim.shipment?.carrier)?.toUpperCase(),
                packageIssue: claim.claimDetails?.claimPackageIssue,
                packageDescription: {
                    outerBox: claim.packaging?.outerBoxDescription,
                    innerBox: claim.packaging?.innerBoxDescription,
                    other: claim.packaging?.otherPackagingDescription,
                },
                recipientContacted: claim.additionalInformation?.recipientContacted || null,
                packageCurrentLocation: claim.additionalInformation?.packageLocation,
                additionalNotes: claim.additionalInformation?.details,
                totalClaimedAmount: claim.merchandises?.reduce((acc, curr) => acc.plus(curr.claimedAmount), new Big(0)).toNumber(),
                paymentMethod: claim.payeeInformation?.paymentMethod,
            },
            products: claim.merchandises?.map(merchandise => ({
                productCategory: merchandise.commodityTypeCode,
                productDescription: merchandise.description,
                noOfItems: merchandise.numberOfItems,
                itemModelNo: merchandise.itemNumber,
                serialNo: merchandise.serialNumber,
                invoiceValue: merchandise.invoiceValue,
                claimedAmount: merchandise.claimedAmount,
            })) || [],
            claimantContactInfo: {
                customerId: claim.customerId,
                ...this.mapClaimContactToApiClaimContact(claim.claimantContact),
            },
            shipperContactInfo: this.mapClaimContactToApiClaimContact(claim.shipment?.shipperContact),
            recipientContactInfo: this.mapClaimContactToApiClaimContact(claim.shipment?.receiverContact),
            mailingAddress: this.mapClaimContactToApiClaimContact(claim.payeeInformation?.mailingAddress),
        } as IApiClaimSubmitRequest;
    }

    private mapClaimContactToApiClaimContact(contact: IClaimContact): IApiClaimContact {
        if (!contact) {
            return null;
        }

        return {
            companyName: contact?.companyName,
            firstName: contact?.firstName,
            lastName: contact?.lastName,
            streetAddress: contact?.address1,
            apartmentSuite: contact?.address2,
            city: contact?.city,
            state: contact?.state,
            zip: contact?.zip,
            country: contact?.countryCode,
            telephoneNo: contact?.phone?.replace(/^\+/, ''),
            email: contact?.email,
        } as IApiClaimContact;
    }

    private mapApiMailingAddressToMailingAddress(apiContact: IApiClaimContact, mailingContact: Location): IClaimContact {
        if (!apiContact) {
            return null;
        }

        return {
            companyName: apiContact?.companyName || mailingContact?.CompanyName,
            firstName: apiContact?.firstName || mailingContact?.FirstName,
            lastName: apiContact?.lastName || mailingContact?.LastName,
            address1: apiContact?.streetAddress || mailingContact?.StreetAddress,
            address2: apiContact?.apartmentSuite || mailingContact?.ApartmentSuite,
            city: apiContact?.city || mailingContact?.City,
            state: apiContact?.state || mailingContact?.State,
            zip: apiContact?.zip || mailingContact?.Zip,
            countryCode: apiContact?.country || mailingContact?.Country,
            phone: apiContact?.telephoneNo?.replace(/^\+/, '') || mailingContact?.TelephoneNo?.replace(/^\+/, ''),
            email: apiContact?.email || mailingContact?.Email,
        } as IClaimContact;
    }

    private mapApiClaimContactToClaimContact(apiContact: IApiClaimContact, userContacts?: IUserContacts): IClaimContact {
        if (!apiContact) {
            return null;
        }

        return {
            companyName: apiContact?.companyName || userContacts?.accountPayableContact?.CompanyName,
            firstName: apiContact?.firstName || userContacts?.accountPayableContact?.FirstName,
            lastName: apiContact?.lastName || userContacts?.accountPayableContact?.LastName,
            address1: apiContact?.streetAddress || userContacts?.accountPayableContact?.StreetAddress,
            address2: apiContact?.apartmentSuite || userContacts?.accountPayableContact?.ApartmentSuite,
            city: apiContact?.city || userContacts?.accountPayableContact?.City,
            state: apiContact?.state || userContacts?.accountPayableContact?.State,
            zip: apiContact?.zip || userContacts?.accountPayableContact?.Zip,
            countryCode: apiContact?.country || userContacts?.accountPayableContact?.Country,
            phone: apiContact?.telephoneNo?.replace(/^\+/, '') || userContacts?.accountPayableContact?.TelephoneNo?.replace(/^\+/, ''),
            email: apiContact?.email || userContacts?.accountPayableContact?.Email,
        } as IClaimContact;
    }

    private mapApiUploadedDocumentsToDocuments(uploadedFiles: IApiClaimUploadedDocument[]): IClaimDocumentUpload[] {
        return uploadedFiles.map(file => ({
            documentId: file.documentId,
            documentType: file.documentType,
            fileName: file.filename,
            mimeType: file.mimeType,
            status: FileUploadChipStatus.NONE,
            submitDate: file.submitDate,
            isPendingDelete: false,
        } as IClaimDocumentUpload)) || [];
    }

    public getClaimCurrency(): string {
        switch(this.appState.user$()?.CountryCode){
            case 'FR':
            case 'IT':
            case 'DE':
                return 'EUR';
            case 'GB':
                return 'GBP';
            case 'HK':
                return 'HKD';
            default:
                return 'USD';
        }
    }

    public getClaimCurrencySymbol(): string {
        switch(this.appState.user$()?.CountryCode){
            case 'FR':
            case 'IT':
            case 'DE':
                return '€';
            case 'GB':
                return '£';
            default:
                return '$';
        }
    }
}
